import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type FavoriteKind = "athlete" | "team" | "product" | "cause";

const CONFIG: Record<FavoriteKind, { table: string; col: string }> = {
  athlete: { table: "athlete_favorites", col: "athlete_id" },
  team: { table: "team_favorites", col: "team_id" },
  product: { table: "product_favorites", col: "product_id" },
  cause: { table: "cause_favorites", col: "cause_id" },
};

export const useFavorites = (kind: FavoriteKind = "athlete") => {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const { table, col } = CONFIG[kind];

  const refresh = useCallback(async () => {
    if (!user) {
      setFavorites(new Set());
      return;
    }
    setLoading(true);
    const { data } = await (supabase as any)
      .from(table)
      .select(col)
      .eq("user_id", user.id);
    setFavorites(new Set((data ?? []).map((r: any) => r[col])));
    setLoading(false);
  }, [user, table, col]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const toggle = useCallback(
    async (targetId: string) => {
      if (!user) return false;
      const has = favorites.has(targetId);
      setFavorites((prev) => {
        const next = new Set(prev);
        if (has) next.delete(targetId);
        else next.add(targetId);
        return next;
      });
      if (has) {
        const { error } = await (supabase as any)
          .from(table)
          .delete()
          .eq("user_id", user.id)
          .eq(col, targetId);
        if (error) refresh();
      } else {
        const { error } = await (supabase as any)
          .from(table)
          .insert({ user_id: user.id, [col]: targetId });
        if (error) refresh();
      }
      return !has;
    },
    [user, favorites, refresh, table, col],
  );

  return { favorites, toggle, loading, refresh };
};
